import { useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type InteractionType = Database["public"]["Tables"]["interactions"]["Row"]["interaction_type"];

const getLeadId = (): string | null => {
  try {
    return localStorage.getItem("leadId") || null;
  } catch {
    return null;
  }
};

const resolveLeadId = async (_email?: string): Promise<string | null> => {
  // Lead ID should be stored in localStorage after registration.
  // The RPC is restricted to authenticated users only (prevents email enumeration).
  return getLeadId();
};

export const trackInteraction = async (
  type: InteractionType,
  extraData?: Record<string, string | number | boolean | null>,
  email?: string
) => {
  const leadId = await resolveLeadId(email);
  if (!leadId) return;

  const payload = {
    lead_id: leadId,
    interaction_type: type,
    page_url: window.location.pathname,
    user_agent: navigator.userAgent,
    interaction_data: extraData || {},
  };

  const { error } = await supabase.from("interactions").insert([payload]);
  if (error) console.error("Tracking failed");
};

type ExtraData = Record<string, string | number | boolean | null>;

export const useInteractionTracking = (
  pageType?: InteractionType,
  extraData?: ExtraData
) => {
  useEffect(() => {
    if (pageType) {
      trackInteraction(pageType, extraData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageType]);

  const track = useCallback(
    (type: InteractionType, data?: ExtraData, email?: string) =>
      trackInteraction(type, data, email),
    []
  );

  return { track };
};

export default useInteractionTracking;
